/**
 * Pure reconciliation between a user's SAVED navigation preference and the
 * currently AUTHORIZED navigation catalog.
 *
 * Rule: a saved preference is never an authorization grant. Authorization comes
 * first (hasPermission → authorized catalog), preference only reorders/filters
 * what is already authorized.
 */

import { BOTTOM_NAV_MAX, DAR_NAV_ID, catalogById, type NavItem } from "@/lib/nav-catalog";

export type DarPlacement = "floating" | "nav_bar";

export type NavPreferences = {
  bottom_nav_order: string[];
  sidebar_order: string[];
  dar_orb_placement: DarPlacement;
};

export const EMPTY_PREFS: NavPreferences = {
  bottom_nav_order: [],
  sidebar_order: [],
  dar_orb_placement: "floating",
};

export type Resolution = {
  /** Effective, render-ready items. */
  items: NavItem[];
  ids: string[];
  /** Saved ids that are no longer authorized/known and were discarded. */
  dropped: string[];
  /** True when the saved preference should be rewritten to `ids`. */
  repaired: boolean;
  /** True when the permission-filtered default was used instead of the saved order. */
  usedDefault: boolean;
};

function knownAuthorized(ids: string[], authorized: NavItem[]): string[] {
  const allowed = new Set(authorized.map((i) => i.id));
  return ids.filter((id, idx) => allowed.has(id) && ids.indexOf(id) === idx);
}

/**
 * Bottom navigation: saved order, filtered to authorized items, capped to the
 * visible limit. Falls back to the permission-filtered default only when zero
 * saved items remain authorized.
 */
export function resolveBottomNav(
  saved: string[],
  authorized: NavItem[],
  defaults: string[],
): Resolution {
  const allowed = new Set(authorized.map((i) => i.id));
  const kept = knownAuthorized(saved, authorized).slice(0, BOTTOM_NAV_MAX);
  const dropped = saved.filter((id) => !allowed.has(id));

  const usedDefault = kept.length === 0;
  const ids = usedDefault ? defaults.slice(0, BOTTOM_NAV_MAX) : kept;
  const repaired = usedDefault ? saved.length > 0 : dropped.length > 0 || kept.length !== saved.length;

  return {
    ids,
    items: ids.map((id) => catalogById(id)!).filter(Boolean),
    dropped,
    repaired,
    usedDefault,
  };
}

/**
 * Sidebar: saved order first (authorized only), then any authorized item the
 * user has never ordered, in catalog order. Never drops an authorized item.
 */
export function resolveSidebar(
  saved: string[],
  authorized: NavItem[],
  defaults: string[],
): Resolution {
  const allowed = new Set(authorized.map((i) => i.id));
  const kept = knownAuthorized(saved, authorized);
  const dropped = saved.filter((id) => !allowed.has(id));

  const usedDefault = kept.length === 0;
  const base = usedDefault ? defaults.filter((id) => allowed.has(id)) : kept;
  const appended = authorized.map((i) => i.id).filter((id) => !base.includes(id));
  const ids = [...base, ...appended];
  const repaired = dropped.length > 0 || (usedDefault && saved.length > 0);

  return {
    ids,
    items: ids.map((id) => catalogById(id)!).filter(Boolean),
    dropped,
    repaired,
    usedDefault,
  };
}

/** Items authorized for the bottom nav but not selected → the "More" overflow. */
export function overflowItems(effectiveIds: string[], authorized: NavItem[]): NavItem[] {
  const selected = new Set(effectiveIds);
  return authorized.filter((i) => !selected.has(i.id));
}

/**
 * DAR placement is a placement preference, never a grant. When DAR is not
 * authorized the placement is repaired back to "floating" (and the orb itself
 * is hidden by the permission check).
 */
export function resolveDarPlacement(
  placement: DarPlacement,
  darAuthorized: boolean,
): { placement: DarPlacement; repaired: boolean } {
  if (!darAuthorized && placement !== "floating") {
    return { placement: "floating", repaired: true };
  }
  return { placement, repaired: false };
}

/** Result of asking to move DAR into the bottom nav. */
export type DarPromotion =
  | { status: "added"; bottom_nav_order: string[] }
  | { status: "full"; bottom_nav_order: string[] };

export function promoteDarToBottomNav(currentIds: string[]): DarPromotion {
  if (currentIds.includes(DAR_NAV_ID)) return { status: "added", bottom_nav_order: currentIds };
  if (currentIds.length < BOTTOM_NAV_MAX) {
    return { status: "added", bottom_nav_order: [...currentIds, DAR_NAV_ID] };
  }
  return { status: "full", bottom_nav_order: currentIds };
}

/** Replace one selected item with DAR, preserving the order of the rest. */
export function replaceWithDar(currentIds: string[], replaceId: string): string[] {
  return currentIds.map((id) => (id === replaceId ? DAR_NAV_ID : id));
}

/** Move an item within an ordered list (drag-to-reorder). */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
